import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { BaseCheckbox } from 'common/components/base-checkbox-group';
import FormProvider from 'common/components/form-provider';
import { timeBasedIntervalsSchema } from 'common/schema';
import React from 'react';
import { useForm } from 'react-hook-form';
import { colors } from 'common/constants';
import { createStyleSheet } from 'utils/helpers';
import lodash from 'lodash';

interface ITimeBasedIntervalsProps {
    intervals: 'monthly' | 'yearly';
    dataJSON: any; // eslint-disable-line
    isUpgrade?: boolean;
}

const TimeBasedIntervals: React.FC<ITimeBasedIntervalsProps> = ({
    intervals,
    dataJSON,
    isUpgrade = false,
}) => {
    const emptyArr = ['', ''];

    const keyPlanDetailArr = lodash.get(dataJSON, 'mainkeys', []);
    const subKeyPlanDetailArr = lodash.get(dataJSON, 'subkeys', []);
    const childKeys = lodash.get(dataJSON, 'childKeys', []);
    const premierPlan = lodash.get(dataJSON, 'premier', {});
    const advancePlan = lodash.get(dataJSON, 'advance', {});
    const standardPlan = lodash.get(dataJSON, 'standard', {});
    const basicPlan = lodash.get(dataJSON, 'basic', {});

    const returnDefaultValues: Record<string, boolean> = {};

    subKeyPlanDetailArr.forEach(
        (subKey: { value: string; displayAs: string }) => {
            if (subKey.displayAs === 'checkbox') {
                childKeys[subKey.value].forEach(
                    (childItem: { id: string; prefix: string }) => {
                        keyPlanDetailArr.forEach((keyPlanDetail: string) => {
                            const keyOfDefaultValues = `${childItem.prefix}_${keyPlanDetail}`;
                            returnDefaultValues[keyOfDefaultValues] =
                                lodash.get(
                                    dataJSON,
                                    `${keyPlanDetail}.${subKey.value}.${childItem.id}`,
                                    false
                                );
                        });
                    }
                );
            }
        }
    );

    const methods = useForm({
        resolver: yupResolver(timeBasedIntervalsSchema),
        defaultValues: returnDefaultValues,
    });
    const onSubmit = methods.handleSubmit((data) => {
        console.log(data);
    });

    return (
        <Box>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <>
                    <TableContainer
                        component={Paper}
                        sx={{
                            boxShadow: 'none',
                            border: 'none',
                        }}
                    >
                        <Table sx={{ borderCollapse: 'collapse' }}>
                            {!isUpgrade && (
                                <TableHead
                                    sx={{
                                        backgroundColor:
                                            'var(--color-primary-lighter)',
                                    }}
                                >
                                    <TableRow>
                                        <TableCell
                                            align={'center'}
                                            sx={{
                                                border: 'none',
                                                fontWeight: 600,
                                                fontSize: '18px',
                                                lineHeight: '21.78px',
                                            }}
                                            colSpan={6}
                                        >
                                            {intervals === 'monthly'
                                                ? 'Gói tháng'
                                                : 'Gói năm'}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        {emptyArr.map(
                                            (planDetailKey, index) => (
                                                <TableCell
                                                    key={`header-empty-${index}`}
                                                    align={'left'}
                                                    sx={{
                                                        border: 'none',
                                                        fontWeight: 600,
                                                        fontSize: '18px',
                                                        lineHeight: '21.78px',
                                                    }}
                                                >
                                                    {planDetailKey}
                                                </TableCell>
                                            )
                                        )}
                                        {keyPlanDetailArr.map(
                                            (
                                                planDetailKey: string,
                                                index: number
                                            ) => (
                                                <TableCell
                                                    key={`header-${index}`}
                                                    align={'left'}
                                                    sx={{
                                                        border: 'none',
                                                        fontWeight: 600,
                                                        fontSize: '18px',
                                                        lineHeight: '21.78px',
                                                    }}
                                                >
                                                    {
                                                        lodash.get(
                                                            dataJSON,
                                                            planDetailKey,
                                                            {}
                                                        ).text
                                                    }
                                                </TableCell>
                                            )
                                        )}
                                    </TableRow>
                                </TableHead>
                            )}

                            <TableBody>
                                {!isUpgrade && (
                                    <TableRow>
                                        <TableCell
                                            align="left"
                                            sx={{ border: 'none' }}
                                        ></TableCell>
                                        <TableCell
                                            align="left"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '18px',
                                                lineHeight: '21.78px',
                                                border: 'none',
                                            }}
                                        >
                                            {dataJSON.text}
                                        </TableCell>
                                        {keyPlanDetailArr.map(
                                            (
                                                planDetailKey: string,
                                                index: number
                                            ) => (
                                                <TableCell
                                                    key={`price-${index}`}
                                                    align="left"
                                                    sx={{ border: 'none' }}
                                                >
                                                    {lodash.get(
                                                        dataJSON,
                                                        `${planDetailKey}.price.text`,
                                                        ''
                                                    )}
                                                </TableCell>
                                            )
                                        )}
                                    </TableRow>
                                )}
                                <TableRow>
                                    <TableCell
                                        align="left"
                                        sx={{ border: 'none' }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '18px',
                                                fontWeight: 700,
                                            }}
                                        >
                                            STT
                                        </Typography>
                                    </TableCell>
                                    <TableCell
                                        colSpan={6}
                                        align="left"
                                        sx={{
                                            border: 'none',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '18px',
                                                fontWeight: 700,
                                            }}
                                        >
                                            Tính năng
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                                {subKeyPlanDetailArr.map(
                                    (subKey: {
                                        text: string;
                                        value: string;
                                        displayAs: string;
                                    }) => {
                                        return (
                                            <>
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={6}
                                                        align="left"
                                                        sx={{
                                                            border: 'none',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '18px',
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {subKey.text}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                                {lodash
                                                    .get(
                                                        childKeys,
                                                        subKey.value,
                                                        []
                                                    )
                                                    .map(
                                                        (childItem: {
                                                            id: string;
                                                            text: string;
                                                            prefix: string;
                                                        }) => {
                                                            return (
                                                                <TableRow
                                                                    key={`${subKey.value}-${childItem.id}`}
                                                                >
                                                                    <TableCell
                                                                        align="left"
                                                                        sx={{
                                                                            border: 'none',
                                                                        }}
                                                                    >
                                                                        {
                                                                            childItem.id
                                                                        }
                                                                    </TableCell>
                                                                    <TableCell
                                                                        align="left"
                                                                        sx={{
                                                                            border: 'none',
                                                                        }}
                                                                    >
                                                                        {
                                                                            childItem.text
                                                                        }
                                                                    </TableCell>
                                                                    {subKey.displayAs ===
                                                                    'text' ? (
                                                                        <>
                                                                            <TableCell
                                                                                align="left"
                                                                                sx={{
                                                                                    border: 'none',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    premierPlan[
                                                                                        subKey
                                                                                            .value
                                                                                    ][
                                                                                        childItem
                                                                                            .id
                                                                                    ]
                                                                                        .text
                                                                                }
                                                                            </TableCell>
                                                                            <TableCell
                                                                                align="left"
                                                                                sx={{
                                                                                    border: 'none',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    advancePlan[
                                                                                        subKey
                                                                                            .value
                                                                                    ][
                                                                                        childItem
                                                                                            .id
                                                                                    ]
                                                                                        .text
                                                                                }
                                                                            </TableCell>
                                                                            <TableCell
                                                                                align="left"
                                                                                sx={{
                                                                                    border: 'none',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    standardPlan[
                                                                                        subKey
                                                                                            .value
                                                                                    ][
                                                                                        childItem
                                                                                            .id
                                                                                    ]
                                                                                        .text
                                                                                }
                                                                            </TableCell>
                                                                            <TableCell
                                                                                align="left"
                                                                                sx={{
                                                                                    border: 'none',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    basicPlan[
                                                                                        subKey
                                                                                            .value
                                                                                    ][
                                                                                        childItem
                                                                                            .id
                                                                                    ]
                                                                                        .text
                                                                                }
                                                                            </TableCell>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <TableCell
                                                                                align="left"
                                                                                sx={{
                                                                                    border: 'none',
                                                                                }}
                                                                            >
                                                                                <BaseCheckbox
                                                                                    name={`${
                                                                                        childItem.prefix
                                                                                    }_${keyPlanDetailArr[0]}`}
                                                                                    label={
                                                                                        undefined
                                                                                    }
                                                                                    disabled={
                                                                                        true
                                                                                    }
                                                                                    sx={
                                                                                        styles.checkbox
                                                                                    }
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell
                                                                                align="left"
                                                                                sx={{
                                                                                    border: 'none',
                                                                                }}
                                                                            >
                                                                                <BaseCheckbox
                                                                                    name={`${
                                                                                        childItem.prefix
                                                                                    }_${keyPlanDetailArr[1]}`}
                                                                                    label={
                                                                                        undefined
                                                                                    }
                                                                                    disabled={
                                                                                        true
                                                                                    }
                                                                                    sx={
                                                                                        styles.checkbox
                                                                                    }
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell
                                                                                align="left"
                                                                                sx={{
                                                                                    border: 'none',
                                                                                }}
                                                                            >
                                                                                <BaseCheckbox
                                                                                    name={`${
                                                                                        childItem.prefix
                                                                                    }_${keyPlanDetailArr[2]}`}
                                                                                    label={
                                                                                        undefined
                                                                                    }
                                                                                    disabled={
                                                                                        true
                                                                                    }
                                                                                    sx={
                                                                                        styles.checkbox
                                                                                    }
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell
                                                                                align="left"
                                                                                sx={{
                                                                                    border: 'none',
                                                                                }}
                                                                            >
                                                                                <BaseCheckbox
                                                                                    name={`${
                                                                                        childItem.prefix
                                                                                    }_${keyPlanDetailArr[3]}`}
                                                                                    label={
                                                                                        undefined
                                                                                    }
                                                                                    disabled={
                                                                                        true
                                                                                    }
                                                                                    sx={
                                                                                        styles.checkbox
                                                                                    }
                                                                                />
                                                                            </TableCell>
                                                                        </>
                                                                    )}
                                                                </TableRow>
                                                            );
                                                        }
                                                    )}
                                            </>
                                        );
                                    }
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            </FormProvider>
        </Box>
    );
};
const styles = createStyleSheet({
    checkbox: {
        '& .MuiSvgIcon-root': {
            color: 'gray',
        },
        '& .Mui-checked.Mui-disabled .MuiSvgIcon-root': {
            color: colors.main,
        },
        '& .Mui-disabled .MuiSvgIcon-root': {
            color: 'gray',
        },
    },
});
export default TimeBasedIntervals;
